import * as React from "react";
import { Box, Typography, Stack, Button, CircularProgress } from "@mui/material";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { fetchOpenWorkshops, chooseWorkshop, fetchMissionsForWorkshop, chooseMission, fetchMyWorkshopStatus } from "../api/queries";
import { getSession } from "../api/session";
import { getMissionIcon } from "./missions/missionIcons";
import { formatPlatform, formatOrbit } from "./missions/platformOrbit";
import type { MissionRow } from "../types/payload";
import type { OpenWorkshop, WorkshopStatus } from "../types/workshop";

// A chosen mission can be swapped for another until this long before the
// workshop starts. The database enforces the same rule (see
// supabase/sql/mission_change_cutoff.sql); this only decides what to offer.
const MISSION_CHANGE_CUTOFF_MS = 2 * 60 * 60 * 1000;

function missionChangeOpen(startsAt: string | null): boolean {
  return !startsAt || Date.now() < new Date(startsAt).getTime() - MISSION_CHANGE_CUTOFF_MS;
}

// "en-US" forces English wording regardless of the system/browser locale;
// the timezone itself is already the computer's local one by default (no
// timeZone override), only the language was following system locale.
function formatWorkshopWhen(startsAt: string | null): { dateStr: string | null; timeStr: string | null } {
  if (!startsAt) return { dateStr: null, timeStr: null };
  const dt = new Date(startsAt);
  return {
    dateStr: dt.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    timeStr: dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };
}

// One workshop's name, date and place. `action` is the button shown beside
// it: "choose" in the workshop chooser, "choose a different one" once chosen.
function WorkshopBanner(props: { workshop: OpenWorkshop; action?: React.ReactNode }) {
  const { workshop } = props;
  const { dateStr, timeStr } = formatWorkshopWhen(workshop.starts_at);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: "1px solid rgba(168,85,247,0.28)",
        background: "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
        backdropFilter: "blur(14px)",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          color: "#E9D5FF",
          background: "linear-gradient(160deg, rgba(124,58,237,0.45), rgba(56,189,248,0.22))",
          border: "1px solid rgba(196,181,253,0.35)",
        }}
      >
        <EventAvailableRoundedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Stack spacing={0.4} sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 15, sm: 16 } }}>{workshop.name}</Typography>
        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13, lineHeight: 1.5 }}>
          {dateStr && (
            <>
              <strong style={{ color: "#E9D5FF" }}>{dateStr}</strong>
              {timeStr && (
                <>
                  {" "}
                  at <strong style={{ color: "#E9D5FF" }}>{timeStr}</strong>
                </>
              )}
            </>
          )}
          {workshop.location && <> {dateStr ? "·" : ""} {workshop.location}</>}
        </Typography>
        {workshop.description && (
          <Typography sx={{ color: "rgba(226,232,240,0.55)", fontSize: 12.5, lineHeight: 1.5 }}>{workshop.description}</Typography>
        )}
      </Stack>
      {props.action && <Box sx={{ flexShrink: 0, alignSelf: "center" }}>{props.action}</Box>}
    </Stack>
  );
}

function MissionTeaser(props: { mission: MissionRow }) {
  const { mission } = props;
  const Icon = getMissionIcon(mission.icon_key);

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(168,85,247,0.28)",
        background: "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
        backdropFilter: "blur(14px)",
      }}
    >
      <Box
        sx={{
          height: 150,
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(9,5,11,0.5)",
          borderBottom: "1px solid rgba(168,85,247,0.2)",
          backgroundImage: mission.image_url ? `url(${mission.image_url})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {!mission.image_url && <ImageNotSupportedRoundedIcon sx={{ fontSize: 30, color: "rgba(226,232,240,0.3)" }} />}
      </Box>

      <Stack spacing={1.5} sx={{ p: { xs: 2.25, sm: 2.75 } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: "#E9D5FF",
              background: "linear-gradient(160deg, rgba(124,58,237,0.45), rgba(56,189,248,0.22))",
              border: "1px solid rgba(196,181,253,0.35)",
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
          <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 18, sm: 19 } }}>{mission.name}</Typography>
        </Stack>

        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13.5, lineHeight: 1.6 }}>{mission.objective}</Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap rowGap={0.75}>
          <Stack spacing={0.25}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(196,181,253,0.6)" }}>
              Platform
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#FFFFFF" }}>{formatPlatform(mission.platform_type)}</Typography>
          </Stack>
          <Stack spacing={0.25}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(196,181,253,0.6)" }}>
              Orbit
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#FFFFFF" }}>{formatOrbit(mission.orbit_type)}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function Dashboard(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  // Workshops still open to choose from, and the one this person chose.
  // With a single open workshop there is nothing to choose: it is picked
  // for them, as before.
  const [openWorkshops, setOpenWorkshops] = React.useState<OpenWorkshop[]>([]);
  const [workshop, setWorkshop] = React.useState<OpenWorkshop | null>(null);
  const [missions, setMissions] = React.useState<MissionRow[]>([]);
  const [status, setStatus] = React.useState<WorkshopStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [choosingId, setChoosingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const mounted = React.useRef(true);

  const session = React.useMemo(() => getSession(), []);

  // Saves the workshop choice (unless it already is the saved one) and loads
  // that workshop's missions for the mission chooser.
  const enterWorkshop = React.useCallback(async (chosen: OpenWorkshop, alreadySaved: boolean) => {
    if (!alreadySaved) {
      const { data: ok, error: chooseError } = await chooseWorkshop(chosen.id);
      if (!mounted.current) return;
      if (chooseError || !ok) {
        setError(chooseError?.message ?? "Couldn't save your choice. Please try again.");
        return;
      }
    }

    const { data: rows, error: missionsError } = await fetchMissionsForWorkshop(chosen.id);
    if (!mounted.current) return;
    if (missionsError) {
      setError(missionsError.message);
      return;
    }

    setMissions(
      (rows ?? [])
        .map((row) => row.missions)
        .filter((m): m is MissionRow => m !== null)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    setWorkshop(chosen);
  }, []);

  React.useEffect(() => {
    mounted.current = true;

    (async () => {
      const [{ data: open, error: workshopError }, statusResult] = await Promise.all([
        fetchOpenWorkshops(),
        session ? fetchMyWorkshopStatus(session.email) : Promise.resolve({ data: null, error: null }),
      ]);

      if (!mounted.current) return;

      if (workshopError) {
        setLoading(false);
        setError(workshopError.message);
        return;
      }

      const workshops = open ?? [];
      setOpenWorkshops(workshops);
      setStatus(statusResult.data ?? null);

      // Carry on with the saved choice while it is still open; otherwise a
      // single open workshop is chosen automatically, and several are left
      // for the person to choose from. Organisers are never enrolled
      // automatically -- they'd show up among their own participants.
      const saved = workshops.find((w) => w.id === statusResult.data?.workshop_id);
      const isParticipant = statusResult.data?.role === "user";
      if (saved) await enterWorkshop(saved, true);
      else if (workshops.length === 1 && isParticipant) await enterWorkshop(workshops[0], false);

      if (mounted.current) setLoading(false);
    })();

    return () => {
      mounted.current = false;
    };
  }, [session, enterWorkshop]);

  const pickWorkshop = async (chosen: OpenWorkshop) => {
    setError(null);
    setChoosingId(chosen.id);
    await enterWorkshop(chosen, false);
    if (mounted.current) setChoosingId(null);
  };

  const pickMission = async (missionId: string) => {
    if (!session) return;
    setError(null);
    setChoosingId(missionId);

    const { data: ok, error: chooseError } = await chooseMission(session.email, missionId);

    setChoosingId(null);

    if (chooseError || !ok) {
      setError(chooseError?.message ?? "Couldn't save your choice. Please try again.");
      return;
    }

    handlePageChange("WorkshopWaiting");
  };

  // On to the group's sheet, or to the group name + code screen while it is still locked.
  const continueToMission = () => {
    if (!status?.chosen_mission_id) return;
    handlePageChange(status.access_verified ? "MissionView" : "WorkshopWaiting");
  };

  const hasMission = Boolean(status?.chosen_mission_id);
  const canChangeMission = missionChangeOpen(status?.workshop_starts_at ?? null);
  const otherMissions = missions.filter((m) => m.id !== status?.chosen_mission_id);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "#08050b",
        backgroundImage:
          "linear-gradient(180deg, rgba(9,5,11,0.94) 0%, rgba(9,5,11,0.82) 32%, rgba(46,16,84,0.55) 100%), url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: { xs: 2.5, sm: 4, md: 6 },
        pt: { xs: 9, sm: 9, md: 8 },
        pb: { xs: 5, sm: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 18% 12%, rgba(168,85,247,0.22), transparent 40%), radial-gradient(circle at 82% 78%, rgba(56,189,248,0.16), transparent 42%)",
          pointerEvents: "none",
        }}
      />

      <Stack spacing={{ xs: 4, md: 5 }} sx={{ width: "100%", maxWidth: 980, position: "relative", zIndex: 1 }}>
        <Stack spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} textAlign={{ xs: "left", sm: "center" }}>
          <Typography
            sx={{ color: "#C4B5FD", fontWeight: 800, letterSpacing: "0.28em", textTransform: "uppercase", fontSize: { xs: 11, sm: 12 } }}
          >
            StellarisMCS
          </Typography>
          <Typography
            sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 32, sm: 42, md: 52 }, lineHeight: 1.08, textShadow: "0 0 40px rgba(168,85,247,0.35)" }}
          >
            Mission Control
          </Typography>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress sx={{ color: "#C4B5FD" }} />
          </Stack>
        ) : hasMission && status ? (
          <>
          <Stack
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            textAlign={{ xs: "left", sm: "center" }}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              border: "1px solid rgba(168,85,247,0.28)",
              background: "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
              backdropFilter: "blur(14px)",
            }}
          >
            <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: 14 }}>
              {status.access_verified ? "You're working on" : "You chose"}
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 28 } }}>{status.mission_name}</Typography>
            {status.group_name && (
              <Typography sx={{ color: "#C4B5FD", fontWeight: 700, fontSize: 14 }}>Group {status.group_name}</Typography>
            )}
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={continueToMission}
              sx={{ bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              Continue to {status.mission_name}
            </Button>
          </Stack>

          {/* Still able to look at what is on offer -- and to switch, until shortly before the workshop. */}
          {workshop && (
            <>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                {error && <Typography sx={{ color: "#FCA5A5", fontSize: 13 }}>{error}</Typography>}

                {!canChangeMission ? (
                  <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 13.5 }}>
                    Your mission can no longer be changed: the workshop starts in less than 2 hours.
                  </Typography>
                ) : (
                  otherMissions.length > 0 && (
                    <>
                      <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 18 } }}>
                        Want a different mission?
                      </Typography>
                      <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 13 }}>
                        You can switch until 2 hours before the workshop starts.
                        {status.access_verified && " Switching takes you out of your current group."}
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        {otherMissions.map((mission) => (
                          <Button
                            key={mission.id}
                            variant="outlined"
                            onClick={() => pickMission(mission.id)}
                            disabled={choosingId !== null}
                            startIcon={
                              choosingId === mission.id ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : undefined
                            }
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              borderRadius: 2,
                              px: 3,
                              color: "#E9D5FF",
                              borderColor: "#7C3AED",
                              "&:hover": { borderColor: "#A78BFA", bgcolor: "rgba(124,58,237,0.2)" },
                            }}
                          >
                            Switch to {mission.name}
                          </Button>
                        ))}
                      </Stack>
                    </>
                  )
                )}
              </Stack>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 2.5, sm: 3 } }}>
                {missions.map((mission) => (
                  <MissionTeaser key={mission.id} mission={mission} />
                ))}
              </Box>
            </>
          )}
          </>
        ) : openWorkshops.length === 0 ? (
          <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 14, textAlign: "center" }}>
            No workshop is currently scheduled. Check back closer to the event.
          </Typography>
        ) : !workshop ? (
          <Stack spacing={2}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 18, sm: 21 }, textAlign: "center" }}>
              Which workshop are you taking part in?
            </Typography>

            {error && <Typography sx={{ color: "#FCA5A5", fontSize: 13, textAlign: "center" }}>{error}</Typography>}

            {openWorkshops.map((w) => (
              <WorkshopBanner
                key={w.id}
                workshop={w}
                action={
                  <Button
                    variant="contained"
                    onClick={() => pickWorkshop(w)}
                    disabled={choosingId !== null}
                    startIcon={choosingId === w.id ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : undefined}
                    sx={{ bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, textTransform: "none", fontWeight: 800, borderRadius: 2 }}
                  >
                    Choose
                  </Button>
                }
              />
            ))}
          </Stack>
        ) : (
          <>
            <WorkshopBanner
              workshop={workshop}
              action={
                openWorkshops.length > 1 ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setError(null);
                      setWorkshop(null);
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, color: "#C4B5FD" }}
                  >
                    Choose a different workshop
                  </Button>
                ) : undefined
              }
            />

            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 18, sm: 21 } }}>
                Which mission do you want to do?
              </Typography>

              {error && <Typography sx={{ color: "#FCA5A5", fontSize: 13 }}>{error}</Typography>}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                {missions.map((mission) => (
                  <Button
                    key={mission.id}
                    variant="contained"
                    size="large"
                    onClick={() => pickMission(mission.id)}
                    disabled={choosingId !== null}
                    startIcon={
                      choosingId === mission.id ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : undefined
                    }
                    sx={{ bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, textTransform: "none", fontWeight: 800, borderRadius: 2, px: 3 }}
                  >
                    {mission.name}
                  </Button>
                ))}
              </Stack>
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 2.5, sm: 3 } }}>
              {missions.map((mission) => (
                <MissionTeaser key={mission.id} mission={mission} />
              ))}
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}
